import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SecurityMonitor = ({ walletAddress, onSettingsClick }) => {
  const [alerts, setAlerts] = useState([
    {
      id: 1,
      type: 'warning',
      title: 'Unusual Transaction Pattern',
      description: 'Multiple small transactions detected in the last hour',
      timestamp: '5 min ago',
      severity: 'medium'
    },
    {
      id: 2,
      type: 'info',
      title: 'New Device Login',
      description: 'Login from Chrome on Windows detected',
      timestamp: '2 hours ago',
      severity: 'low'
    }
  ]);

  const [securitySettings, setSecuritySettings] = useState({
    transactionApproval: true,
    largeTransactionAlert: true,
    newDeviceAlert: true,
    suspiciousActivityAlert: true
  });

  const securityScore = 85;

  const getAlertIcon = (type) => {
    switch (type) {
      case 'warning': return 'AlertTriangle';
      case 'error': return 'AlertOctagon';
      case 'info': return 'Info';
      default: return 'Bell';
    }
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-info';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-foreground mb-1">Security Monitor</h3>
          <p className="text-sm text-muted-foreground">Wallet protection and alerts</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Settings"
          onClick={onSettingsClick}
        >
          Settings
        </Button>
      </div>
      {/* Security Score */}
      <div className="p-4 bg-gradient-to-r from-primary/20 to-accent/20 rounded-lg mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon name="Shield" size={24} className="text-primary" />
            <p className="text-sm font-medium text-foreground">Security Score</p>
          </div>
          <p className="text-3xl font-bold text-foreground">{securityScore}/100</p>
        </div>
        <div className="w-full bg-muted/50 rounded-full h-2 mb-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all"
            style={{ width: `${securityScore}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {securityScore >= 80 ? 'Excellent security posture' : 
           securityScore >= 60 ? 'Good security, some improvements needed': 'Security needs attention'}
        </p>
      </div>
      {/* Security Features */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <p className="text-xs font-medium text-foreground">2FA Active</p>
          </div>
          <p className="text-xs text-muted-foreground">Two-factor enabled</p>
        </div>

        <div className="p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Lock" size={16} className="text-success" />
            <p className="text-xs font-medium text-foreground">Wallet Encrypted</p>
          </div>
          <p className="text-xs text-muted-foreground">Private keys secure</p>
        </div>

        <div className="p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Eye" size={16} className="text-primary" />
            <p className="text-xs font-medium text-foreground">Monitoring Active</p>
          </div>
          <p className="text-xs text-muted-foreground">24/7 surveillance</p>
        </div>

        <div className="p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Fingerprint" size={16} className="text-success" />
            <p className="text-xs font-medium text-foreground">Biometric</p>
          </div>
          <p className="text-xs text-muted-foreground">Fingerprint enabled</p>
        </div>
      </div>
      {/* Recent Alerts */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-foreground">Recent Alerts</p>
          <button className="text-xs text-primary hover:underline">
            View All
          </button>
        </div>

        <div className="space-y-3">
          {alerts?.length > 0 ? (
            alerts?.map(alert => (
              <div 
                key={alert?.id}
                className="p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <Icon 
                    name={getAlertIcon(alert?.type)} 
                    size={18} 
                    className={getAlertColor(alert?.severity)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground mb-1">{alert?.title}</p>
                    <p className="text-xs text-muted-foreground mb-1">{alert?.description}</p>
                    <p className="text-xs text-muted-foreground">{alert?.timestamp}</p>
                  </div>
                  <button className="text-muted-foreground hover:text-foreground">
                    <Icon name="X" size={14} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No security alerts</p>
            </div>
          )}
        </div>
      </div>
      {/* Transaction Approval Settings */}
      <div className="mt-6 pt-6 border-t border-border">
        <p className="text-sm font-semibold text-foreground mb-3">Transaction Approval</p>
        <div className="space-y-2">
          <label className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/30 cursor-pointer">
            <span className="text-sm text-foreground">Require approval for all transactions</span>
            <input 
              type="checkbox" 
              checked={securitySettings?.transactionApproval}
              onChange={(e) => setSecuritySettings(prev => ({
                ...prev,
                transactionApproval: e?.target?.checked
              }))}
              className="w-4 h-4"
            />
          </label>
          <label className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/30 cursor-pointer">
            <span className="text-sm text-foreground">Alert on large transactions (&gt;$1000)</span>
            <input 
              type="checkbox" 
              checked={securitySettings?.largeTransactionAlert}
              onChange={(e) => setSecuritySettings(prev => ({
                ...prev,
                largeTransactionAlert: e?.target?.checked
              }))}
              className="w-4 h-4"
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default SecurityMonitor;
